import { NgClass, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import {
  ColDef,
  ColumnMovedEvent,
  ColumnState,
  ComponentStateChangedEvent,
  GridOptions,
  GridReadyEvent,
  ModelUpdatedEvent,
  PaginationChangedEvent,
  RowDragEndEvent,
  RowDragMoveEvent,
  SortChangedEvent,
} from 'ag-grid-community';
import { PaginationConstants } from 'app/shared/constants/pagination.const';
import { FeatureFlags } from 'app/shared/services';
import { ContextService } from 'core/services/context.service';
import { ThemeService } from 'core/services/theme.service';
import { ButtonComponent } from 'shared/components/atoms';
import { ButtonColor, ButtonShape, ButtonSize } from 'shared/enums';
import { DataTableLoadingRendererComponent } from '../data-table-loading-renderer/data-table-loading-renderer.component';

@Component({
  selector: 'app-data-table',
  templateUrl: './data-table.component.html',
  styles: [
    `
      :host {
        flex-grow: 1;
        height: 100%;
        position: relative;
        box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent, MatTooltipModule, TranslateModule, AgGridModule, NgClass, NgIf],
})
export class DataTableComponent<T> implements OnChanges {
  protected readonly ButtonShape = ButtonShape;
  protected readonly ButtonSize = ButtonSize;
  protected readonly ButtonColor = ButtonColor;

  private translateSvc = inject(TranslateService);
  private destroyRef = inject(DestroyRef);
  private readonly defaultGridOptions: GridOptions<T> = {
    paginationAutoPageSize: true,
    suppressDragLeaveHidesColumns: true,
    enableBrowserTooltips: false,
    suppressColumnVirtualisation: true,
    autoSizeStrategy: {
      type: 'fitCellContents',
    },
  };
  @Input() public tableName!: string;
  @Input() public rowData: null | T[] = null;
  @Input() public colDefs: ColDef[] | null = [];
  @Input() public paginationPageSize = PaginationConstants.DEFAULT_PAGE_SIZE;
  @Input() public showPagination = true;
  @Input() public dragAndDropEnabled = false;
  @Input() public noRowsTemplate = `<span>${this.translateSvc.instant('global.noData')}</span>`;

  @Output() public gridReady = new EventEmitter<GridReadyEvent>();
  @Output() public dragEnd = new EventEmitter<RowDragEndEvent>();
  @Output() public dragMove = new EventEmitter<RowDragMoveEvent>();
  @Output() public columnMoved = new EventEmitter<ColumnMovedEvent<T>>();
  @Output() public modelUpdated = new EventEmitter<ModelUpdatedEvent>();
  @Output() public sortChanged = new EventEmitter<SortChangedEvent<T>>();
  @Output() public pageSizeChanged = new EventEmitter<number>();
  @Input() public options: GridOptions<T> = this.defaultGridOptions;
  @HostBinding('class') public tableThemeClass = 'ag-theme-material';
  @ViewChild('gridComponent') private gridComponent!: AgGridAngular;
  @ViewChild('gridComponent', { read: ElementRef }) private gridComponentElement!: ElementRef;
  @ViewChild('resetColumnsBtn') private resetColumnsBtn!: ButtonComponent;

  public isGridVisible = true;
  public loadingOverlayComponent = DataTableLoadingRendererComponent;
  private readonly contextSvc = inject(ContextService);

  constructor(
    private themeService: ThemeService,
    private cdRef: ChangeDetectorRef,
  ) {
    this.themeService.darkMode$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((darkMode) => {
      this.tableThemeClass = darkMode ? 'ag-theme-material-dark' : 'ag-theme-material';
      this.cdRef.markForCheck();
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']?.currentValue) {
      this.options = {
        ...this.defaultGridOptions,
        ...changes['options'].currentValue,
      };
    }
  }

  public onGridReady(params: GridReadyEvent): void {
    this.gridReady.emit(params);
    this.configureResetButtonEvents();
  }

  public onStateChanged(params: ComponentStateChangedEvent): void {
    if (this.tableName) {
      params.api.applyColumnState({
        state: this.contextSvc.getItem<ColumnState[]>(this.tableName),
        applyOrder: true,
      });
    }
  }

  public emitMove(event: RowDragMoveEvent): void {
    if (this.dragAndDropEnabled) {
      this.dragMove.emit(event);
    }
  }

  public emitEnd(event: RowDragEndEvent): void {
    if (this.dragAndDropEnabled) {
      this.dragEnd.emit(event);
    }
  }

  public emitColumnMoved(event: ColumnMovedEvent<T>): void {
    this.columnMoved.emit(event);
    if (event.finished && this.tableName && event.source === 'uiColumnMoved') {
      const cs: ColumnState[] = event.api.getColumnState();

      this.contextSvc.storeItem(this.tableName, cs);
    }
  }

  public emitPageSizeChanged(ev: PaginationChangedEvent): void {
    if (ev.newPageSize) {
      const newPageSize = ev.api.paginationGetPageSize();
      this.pageSizeChanged.emit(newPageSize);
    }
  }

  public onSortChanged(event: SortChangedEvent<T>): void {
    if (this.contextSvc.isFeatureFlagEnabled(FeatureFlags.SaveTableSortingState)) {
      const cs: ColumnState[] = event.api.getColumnState();
      this.contextSvc.storeItem(this.tableName, cs);
    }

    if (this.contextSvc.isFeatureFlagEnabled(FeatureFlags.DoneTaskSorting)) {
      this.sortChanged.emit(event);
    }
  }

  public emitModelUpdated(ev: ModelUpdatedEvent): void {
    this.modelUpdated.emit(ev);
    this.resizeColumns();
  }

  public resetColumns(): void {
    this.gridComponent.api.resetColumnState();
    this.resizeColumns();
    // Timeout is so that we don't end up storing the default value
    // since reset triggers an update.
    setTimeout(() => {
      this.contextSvc.removeItem(this.tableName);
    });
  }

  private configureResetButtonEvents(): void {
    const headerElement = this.gridComponentElement.nativeElement.querySelector('.ag-header');
    const buttonElement = this.resetColumnsBtn.element.nativeElement;

    function setZIndex(hoverTarget: HTMLElement): void {
      hoverTarget.onmouseenter = () => {
        buttonElement.style.zIndex = 20;
      };
      hoverTarget.onmouseleave = () => {
        buttonElement.style.zIndex = 0;
      };
    }

    setZIndex(headerElement);
    setZIndex(buttonElement);
  }

  private resizeColumns(): void {
    if (this.gridComponent.api.getGridOption('autoSizeStrategy')?.type == 'fitGridWidth') {
      this.gridComponent.api.sizeColumnsToFit();
    } else {
      this.gridComponent.api.autoSizeAllColumns();
    }
  }
}
